import AltRouteIcon from '@mui/icons-material/AltRoute';
import BarChartIcon from '@mui/icons-material/BarChart';
import BoltIcon from '@mui/icons-material/Bolt';
import ForumIcon from '@mui/icons-material/Forum';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import LockIcon from '@mui/icons-material/Lock';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PieChartIcon from '@mui/icons-material/PieChart';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SendIcon from '@mui/icons-material/Send';
import ShieldIcon from '@mui/icons-material/Shield';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import StorageIcon from '@mui/icons-material/Storage';
import SupportIcon from '@mui/icons-material/Support';
import TranslateIcon from '@mui/icons-material/Translate';
import VoicemailIcon from '@mui/icons-material/Voicemail';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { Box, Link, Typography } from '@mui/material';
import { useEffect, useId, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import CallFlowIllustration from '../components/CallFlowIllustration';
import { StoryPageLayout } from '../layouts';

const PAGE_URL = 'https://www.rsmc.ai/case-studies/nonprofit-amazon-connect';
const PAGE_IMAGE =
  'https://www.rsmc.ai/images/nonprofit-connect-call-center.jpg';
const PAGE_TITLE = 'Every Call Captured, Every Youth Served';
const PAGE_DESCRIPTION =
  'Learn how a California nonprofit serving homeless youth used Amazon Connect with RSMC to achieve 100% call capture, bilingual intake, and monthly costs under $100.';

// Common paragraph styling
const paragraphSx = {
  fontSize: '1rem',
  lineHeight: 1.8,
  color: 'var(--color-text)',
  mb: 3,
};

// Section heading styling
const headingSx = {
  fontWeight: 700,
  mb: 3,
  color: 'var(--color-heading)',
  fontSize: { xs: '1.25rem', md: '1.5rem' },
};

// Card surface shared by the stats, features and architecture grids
const cardSx = {
  border: '1px solid var(--color-border)',
  borderRadius: '12px',
  backgroundColor: 'var(--color-card)',
};

// Muted body copy inside cards
const cardBodySx = {
  fontSize: '0.9375rem',
  lineHeight: 1.7,
  color: 'var(--color-text-muted)',
};

/**
 * Inline link styled to the theme accent. External links open in a new tab.
 *
 * @param {Object} props - Component props
 * @param {string} props.href - Link target
 * @param {React.ReactNode} props.children - Link text
 * @returns {import('react').ReactElement} A themed link
 */
function TextLink({ href, children }) {
  const external = href.startsWith('http');
  return (
    <Link
      href={href}
      {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
      sx={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
    >
      {children}
    </Link>
  );
}

/**
 * Small uppercase label that introduces a section ("The Opportunity", ...).
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Label text
 * @returns {import('react').ReactElement} A section kicker
 */
function Kicker({ children }) {
  return (
    <Typography
      component="span"
      sx={{
        display: 'block',
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--color-primary)',
        mt: 6,
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Icon in a tinted rounded square, used on feature and architecture cards.
 *
 * @param {Object} props - Component props
 * @param {import('react').ElementType} props.icon - MUI icon component
 * @returns {import('react').ReactElement} An icon badge
 */
function IconBadge({ icon: Icon }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: '8px',
        backgroundColor:
          'color-mix(in srgb, var(--color-primary) 15%, transparent)',
        color: 'var(--color-primary)',
      }}
    >
      <Icon sx={{ fontSize: 20 }} />
    </Box>
  );
}

/**
 * Number that counts up from zero the first time it scrolls into view. It
 * renders the final value until then, so reduced-motion visitors, full-page
 * screenshots and anything off-screen always see the real figure.
 *
 * @param {Object} props - Component props
 * @param {number} props.end - Final value
 * @returns {import('react').ReactElement} The animated number
 */
function CountUp({ end }) {
  const ref = useRef(null);
  const [value, setValue] = useState(end);

  useEffect(() => {
    const node = ref.current;
    if (
      !node ||
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }

    let frame;
    let settle;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }
        observer.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - start) / 900, 1);
          setValue(Math.round(end * (1 - (1 - progress) ** 3)));
          if (progress < 1) {
            frame = requestAnimationFrame(tick);
          }
        };
        frame = requestAnimationFrame(tick);
        // Browsers pause animation frames in background tabs; never leave a
        // half-counted figure on screen.
        settle = setTimeout(() => {
          cancelAnimationFrame(frame);
          setValue(end);
        }, 1000);
      },
      { threshold: 0.2 }
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(settle);
    };
  }, [end]);

  return <span ref={ref}>{value}</span>;
}

const keyResults = [
  {
    value: 100,
    unit: '%',
    label: 'voicemail and call capture across all contact types',
  },
  { value: 60, unit: '%', label: 'below the monthly communications budget' },
  {
    value: 2,
    unit: 'hrs',
    label: 'saved every day on daily intake operations',
  },
  { value: 2, unit: '', label: 'languages supported for youth intake' },
];

const growthFigures = [
  { figure: '200+', label: 'youth housed each night' },
  { figure: '400+', label: 'transition-age youth served each year' },
  { figure: 'Multiple', label: 'shelter and housing sites' },
];

const callFlowSteps = [
  {
    icon: PhoneInTalkIcon,
    title: 'Caller chooses a path',
    service: 'Amazon Connect IVR',
    detail:
      'Amazon Connect answers every call with a clear menu that routes youth intake, volunteer, and donation calls to the right staff member or department.',
  },
  {
    icon: TranslateIcon,
    title: 'English or Spanish',
    service: 'Amazon Polly',
    detail:
      'Callers can choose a full Spanish-language path with natural-sounding prompts from Amazon Polly neural text-to-speech, so every family is served in their preferred language.',
  },
  {
    icon: VoicemailIcon,
    title: 'Voicemail becomes text',
    service: 'Amazon Transcribe',
    detail:
      'When a call goes to voicemail, AWS Lambda kicks off the voicemail workflow: Amazon Transcribe converts the recording to searchable text in English or Spanish, and the audio is stored securely in Amazon S3 with AWS KMS encryption.',
  },
  {
    icon: SendIcon,
    title: 'Intake team takes action',
    service: 'Amazon SES · SMS',
    detail:
      "Amazon SES delivers the transcript and a secure audio link to the intake team, and AWS End User Messaging can text an intake form link to the caller's verified number.",
  },
];

const solutionFeatures = [
  {
    icon: AltRouteIcon,
    heading: 'Smart IVR routing',
    body: 'Each caller reaches the right staff member or department, whether the call is about youth intake, volunteering, or giving.',
  },
  {
    icon: MarkEmailReadIcon,
    heading: 'Automated voicemail transcription',
    body: 'AWS Lambda kicks off the voicemail workflow. Amazon Transcribe converts the recording to text, and Amazon SES delivers the transcript and a secure audio link.',
  },
  {
    icon: ForumIcon,
    heading: 'Full Spanish-language path',
    body: 'Amazon Polly neural voices deliver natural Spanish prompts, and Amazon Transcribe supports Spanish voicemail transcription.',
  },
  {
    icon: SmartphoneIcon,
    heading: 'Privacy-first SMS intake',
    body: 'AWS End User Messaging texts intake form links only to the verified number of the incoming call, designed to support TCPA compliance.',
  },
  {
    icon: ShieldIcon,
    heading: 'Secure by design',
    body: 'Recordings are stored in Amazon S3 and encrypted with AWS KMS, with least-privilege access through AWS IAM. AWS CloudFormation deploys every component as code, with automated rollback.',
  },
  {
    icon: MonitorHeartIcon,
    heading: 'Resilient and observable',
    body: 'Amazon CloudWatch and Amazon SNS provide monitoring and alerts, and an Amazon SQS dead-letter queue retains any failed events for recovery.',
  },
];

const analyticsSteps = [
  {
    icon: StorageIcon,
    heading: '1 · Extract',
    body: 'Amazon Connect Contact Trace Records supply caller metadata, timestamps, queue, IVR, transfer, and voicemail details.',
  },
  {
    icon: QueryStatsIcon,
    heading: '2 · Analyze',
    body: 'Amazon Athena views reconstruct caller journeys, identify menu navigation behavior, and classify final call outcomes.',
  },
  {
    icon: PieChartIcon,
    heading: '3 · Visualize',
    body: 'Amazon Quick Sight dashboards turn caller behavior and call handling outcomes into operational insight.',
  },
];

// Each item is [service name(s) in bold, the rest of the sentence]
const architectureGroups = [
  {
    icon: HeadsetMicIcon,
    heading: 'Contact center and voice',
    items: [
      [
        'Amazon Connect',
        ' for cloud-based inbound voice, IVR routing, queues, and real-time and historical reporting',
      ],
      [
        'Amazon Polly',
        ' for neural text-to-speech prompts in the Spanish-language call flow',
      ],
      [
        'Amazon Transcribe',
        ' to convert voicemail audio into searchable text in English and Spanish',
      ],
    ],
  },
  {
    icon: BarChartIcon,
    heading: 'IVR analytics and monitoring',
    featured: true,
    items: [
      [
        'Amazon Connect Contact Trace Records',
        ' (contact_record table) as the source dataset for caller metadata, timestamps, queue information, IVR attributes, transfers, voicemail, and agent interactions',
      ],
      [
        'Amazon Athena',
        ' analytical views to reconstruct caller journeys through the IVR, identify menu navigation behavior, classify final call outcomes, and analyze transfer and voicemail behavior',
      ],
      [
        'Amazon Quick Sight',
        ', part of Amazon Quick, for dashboards that visualize caller behavior and call handling outcomes',
      ],
      [
        'Amazon CloudWatch and Amazon SNS',
        ' for monitoring, alerting, and visibility into Lambda executions and call processing health',
      ],
    ],
  },
  {
    icon: BoltIcon,
    heading: 'Event processing and data capture',
    items: [
      [
        'AWS Lambda',
        ' (Voicemail Express functions) for modular, event-driven voicemail processing and automatic scaling',
      ],
      [
        'Amazon Kinesis Video Streams and Amazon Kinesis Data Streams',
        ' to capture call audio and Contact Trace Records for downstream processing',
      ],
      [
        'Amazon S3',
        ' for durable, encrypted storage of voicemail audio and transcripts',
      ],
    ],
  },
  {
    icon: SendIcon,
    heading: 'Messaging and delivery',
    items: [
      [
        'Amazon Simple Email Service (SES)',
        ' to deliver voicemail transcriptions and secure audio links to an organization-wide distribution list',
      ],
      [
        'AWS End User Messaging',
        ' to text intake form links to the verified number of the incoming call',
      ],
    ],
  },
  {
    icon: SupportIcon,
    heading: 'Resilience',
    items: [
      [
        'Amazon SQS',
        ' as a dead-letter queue that retains failed invocations for investigation and recovery, enabling graceful degradation',
      ],
    ],
  },
  {
    icon: LockIcon,
    heading: 'Security and deployment',
    items: [
      [
        'AWS Identity and Access Management (IAM), AWS Key Management Service (KMS)',
        ', and encryption in Amazon S3 and Amazon Kinesis for least-privilege access, key management, and data protection in transit and at rest',
      ],
      [
        'AWS CloudFormation',
        ' for infrastructure-as-code deployment of Amazon Connect integrations, Lambda functions, and supporting resources with automated rollback',
      ],
    ],
  },
];

const atAGlance = [
  ['Organization', 'California nonprofit serving homeless youth'],
  ['Industry', 'Nonprofit'],
  ['Location', 'California'],
  ['Solution', 'Amazon Connect, Amazon Athena, and Amazon Quick Sight'],
  ['Partner', 'RSMC'],
];

/**
 * Four headline results as stat cards with count-up numbers.
 *
 * @returns {import('react').ReactElement} The key results row
 */
function KeyResults() {
  return (
    <Box
      component="ul"
      aria-label="Key results"
      sx={{
        listStyle: 'none',
        p: 0,
        m: 0,
        mb: 2,
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 2,
      }}
    >
      {keyResults.map((result) => (
        <Box
          component="li"
          key={result.label}
          sx={{
            ...cardSx,
            borderTop: '3px solid var(--color-primary)',
            p: 2.25,
          }}
        >
          <Typography
            sx={{
              fontSize: '2.25rem',
              fontWeight: 800,
              lineHeight: 1,
              color: 'var(--color-primary)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <CountUp end={result.value} />
            {result.unit && (
              <Box
                component="small"
                sx={{ fontSize: '1rem', fontWeight: 700, ml: 0.5 }}
              >
                {result.unit}
              </Box>
            )}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              lineHeight: 1.45,
              color: 'var(--color-text-muted)',
              mt: 1.25,
            }}
          >
            {result.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Three-up strip of the organization's scale figures.
 *
 * @returns {import('react').ReactElement} The growth strip
 */
function GrowthStrip() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        gap: '1px',
        backgroundColor: 'var(--color-border)',
        border: '1px solid var(--color-border)',
        borderRadius: '12px',
        overflow: 'hidden',
        mb: 3,
      }}
    >
      {growthFigures.map((item) => (
        <Box
          key={item.label}
          sx={{ backgroundColor: 'var(--color-background-light)', p: 2.25 }}
        >
          <Typography
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontSize: '1.625rem',
              fontWeight: 600,
              color: 'var(--color-heading)',
            }}
          >
            {item.figure}
          </Typography>
          <Typography
            sx={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * "How a call flows" explorer: four step tabs with a detail panel underneath.
 *
 * @returns {import('react').ReactElement} The interactive call flow
 */
function CallFlowSteps() {
  const [active, setActive] = useState(0);
  const panelId = useId();

  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 2.75 }, my: 4 }}>
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--color-text-muted)',
          mb: 2,
        }}
      >
        How a call flows · select a step
      </Typography>
      <Box
        role="tablist"
        aria-label="Call flow steps"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 1.25,
        }}
      >
        {callFlowSteps.map((step, index) => {
          const Icon = step.icon;
          const selected = index === active;
          return (
            <Box
              key={step.title}
              component="button"
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={panelId}
              onClick={() => setActive(index)}
              sx={{
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                color: 'var(--color-heading)',
                backgroundColor: 'var(--color-background-light)',
                border: '1px solid',
                borderColor: selected
                  ? 'var(--color-primary)'
                  : 'var(--color-border)',
                borderRadius: '8px',
                p: 2,
                transform: selected ? 'translateY(-2px)' : 'none',
                transition: 'border-color 0.15s ease, transform 0.15s ease',
                '&:hover': {
                  borderColor: 'var(--color-primary)',
                  transform: 'translateY(-2px)',
                },
                '&:focus-visible': {
                  outline: '2px solid var(--color-focus-ring)',
                  outlineOffset: '2px',
                },
              }}
            >
              <Icon
                aria-hidden="true"
                sx={{ fontSize: 22, color: 'var(--color-primary)' }}
              />
              <Box
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  lineHeight: 1.35,
                  mt: 1.25,
                  mb: 0.5,
                }}
              >
                {step.title}
              </Box>
              <Box
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '0.75rem',
                  color: 'var(--color-text-muted)',
                }}
              >
                {step.service}
              </Box>
            </Box>
          );
        })}
      </Box>
      <Box
        id={panelId}
        role="tabpanel"
        aria-live="polite"
        sx={{
          mt: 2,
          p: 2,
          borderRadius: '8px',
          backgroundColor:
            'color-mix(in srgb, var(--color-primary) 14%, transparent)',
          color: 'var(--color-text)',
          fontSize: '0.875rem',
          lineHeight: 1.6,
          minHeight: 70,
        }}
      >
        <strong>{callFlowSteps[active].title}.</strong>{' '}
        {callFlowSteps[active].detail}
      </Box>
    </Box>
  );
}

/**
 * Two-column grid of solution capabilities.
 *
 * @returns {import('react').ReactElement} The feature grid
 */
function SolutionFeatures() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        gap: 2,
        mb: 3,
      }}
    >
      {solutionFeatures.map((feature) => (
        <Box key={feature.heading} sx={{ ...cardSx, p: 2.5 }}>
          <IconBadge icon={feature.icon} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              color: 'var(--color-heading)',
              mt: 1.5,
              mb: 1,
            }}
          >
            {feature.heading}
          </Typography>
          <Typography sx={cardBodySx}>{feature.body}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Customer analytics spotlight: the Quick Sight Sankey chart plus the
 * extract / analyze / visualize pipeline.
 *
 * @returns {import('react').ReactElement} The analytics spotlight section
 */
function AnalyticsSpotlight() {
  const headingId = useId();

  return (
    <Box
      component="section"
      aria-labelledby={headingId}
      sx={{
        mt: 6,
        mb: 2,
        p: { xs: 2.5, md: 3.25 },
        borderRadius: '12px',
        border:
          '1px solid color-mix(in srgb, var(--color-primary) 35%, transparent)',
        background:
          'linear-gradient(160deg, color-mix(in srgb, var(--color-primary) 10%, transparent), color-mix(in srgb, #3A6EA5 10%, transparent) 70%)',
      }}
    >
      <Kicker>Spotlight · Customer analytics</Kicker>
      <Typography variant="h2" id={headingId} sx={headingSx}>
        A customer analytics dashboard built on call metrics
      </Typography>
      <Typography sx={paragraphSx}>
        To give leadership a clear view of how the community reaches the
        organization, RSMC built an IVR analytics pipeline using Amazon Connect
        Contact Trace Records,{' '}
        <TextLink href="https://aws.amazon.com/athena/">Amazon Athena</TextLink>
        , and Amazon Quick Sight, the business intelligence capability of{' '}
        <TextLink href="https://aws.amazon.com/quick">Amazon Quick</TextLink>.
        The dashboards show how callers move through the IVR, which menu options
        they choose, how each call ends, and how transfers and voicemail are
        handled.
      </Typography>

      <Box
        component="figure"
        sx={{
          m: 0,
          mb: 3,
          backgroundColor: 'var(--color-background)',
          border: '1px solid var(--color-border)',
          borderRadius: '10px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 1.75,
            py: 1.25,
            borderBottom: '1px solid var(--color-border)',
            fontSize: '0.8125rem',
          }}
        >
          {[0, 1, 2].map((dot) => (
            <Box
              key={dot}
              component="span"
              aria-hidden="true"
              sx={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                backgroundColor: 'var(--color-border)',
              }}
            />
          ))}
          <Box
            component="span"
            sx={{ ml: 0.75, fontWeight: 700, color: 'var(--color-heading)' }}
          >
            Caller journeys through the IVR
          </Box>
          <Box
            component="span"
            sx={{
              ml: 'auto',
              fontSize: '0.6875rem',
              fontWeight: 700,
              color: 'var(--color-primary)',
              border:
                '1px solid color-mix(in srgb, var(--color-primary) 50%, transparent)',
              borderRadius: '100px',
              px: 1.25,
              py: 0.25,
            }}
          >
            Amazon Quick Sight
          </Box>
        </Box>
        {/* The chart is a white-background export, so it keeps a white mat in both modes */}
        <Box sx={{ backgroundColor: '#ffffff', px: 1.5, py: 1.75 }}>
          <Box
            component="img"
            src="/images/nonprofit-connect-ivr-sankey.png"
            alt="Sankey chart from the Amazon Quick Sight dashboard showing distinct callers moving from the main IVR menu to a second menu selection. First-level options are Donation, Volunteer, Trade School, and Accounting and Finance, leading to options such as Make Donation, Sponsorship, Intake, General, Volunteer coordinator and voicemail, Trade School youth and school contacts, and Accounting and Finance donation receipts and payments."
            loading="lazy"
            sx={{ display: 'block', width: '100%', height: 'auto' }}
          />
        </Box>
        <Box
          component="figcaption"
          sx={{
            fontSize: '0.75rem',
            color: 'var(--color-text-muted)',
            px: 1.75,
            py: 1,
            borderTop: '1px solid var(--color-border)',
          }}
        >
          Distinct callers from the first to the second IVR menu selection, from
          the nonprofit&rsquo;s dashboard in Amazon Quick Sight. Dates and
          identifying details removed.
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {analyticsSteps.map((step) => {
          const Icon = step.icon;
          return (
            <Box key={step.heading}>
              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: 'var(--color-heading)',
                  mb: 0.5,
                }}
              >
                <Icon
                  aria-hidden="true"
                  sx={{ fontSize: 18, color: 'var(--color-primary)' }}
                />
                {step.heading}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  lineHeight: 1.55,
                  color: 'var(--color-text-muted)',
                }}
              >
                {step.body}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * Open typographic pull quote with attribution, matching the Rising Routes
 * case study treatment.
 *
 * @param {Object} props - Component props
 * @param {string} props.quote - The quoted text
 * @param {string} props.speaker - Speaker's title
 * @param {string} props.organization - Speaker's organization
 * @returns {import('react').ReactElement} A pull quote
 */
function PullQuote({ quote, speaker, organization }) {
  return (
    <Box
      component="figure"
      sx={{
        my: { xs: 6, md: 8 },
        mx: 0,
        pl: { xs: 1, md: 6 },
        pr: { xs: 1, md: 2 },
      }}
    >
      <Typography
        component="span"
        aria-hidden="true"
        sx={{
          display: 'block',
          fontFamily: '"Playfair Display", serif',
          fontSize: '4rem',
          lineHeight: 0.6,
          color: 'var(--color-primary)',
          mb: 2,
        }}
      >
        &ldquo;
      </Typography>
      <Typography
        component="blockquote"
        sx={{
          fontSize: { xs: '1.25rem', md: '1.5rem' },
          lineHeight: 1.5,
          fontWeight: 600,
          color: 'var(--color-heading)',
          fontStyle: 'italic',
          m: 0,
          mb: 2,
        }}
      >
        {quote}
      </Typography>
      <Typography
        component="figcaption"
        sx={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}
      >
        <Box
          component="strong"
          sx={{ color: 'var(--color-heading)', fontWeight: 700 }}
        >
          {speaker}
        </Box>
        , {organization}
      </Typography>
    </Box>
  );
}

/**
 * Dashed callout for the HubLife follow-on engagement.
 *
 * @returns {import('react').ReactElement} The next-step callout
 */
function NextStepCallout() {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        mt: 4,
        mb: 2,
        p: { xs: 2.5, md: 3 },
        borderRadius: '12px',
        border:
          '1px dashed color-mix(in srgb, var(--color-primary) 50%, transparent)',
        backgroundColor:
          'color-mix(in srgb, var(--color-primary) 5%, transparent)',
      }}
    >
      <VolunteerActivismIcon
        aria-hidden="true"
        sx={{ fontSize: 26, color: 'var(--color-primary)', mt: 0.25 }}
      />
      <Box>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            color: 'var(--color-heading)',
            mb: 0.75,
          }}
        >
          End-to-end grant management with Agentic workflows
        </Typography>
        <Typography sx={{ ...cardBodySx, fontSize: '0.875rem' }}>
          Building on this foundation, the nonprofit is working with RSMC on{' '}
          <TextLink href="https://aws.amazon.com/marketplace/pp/prodview-jmb7ypnpt7kwi">
            HubLife&trade; for Nonprofits
          </TextLink>
          , agentic operations for Amazon Quick, to bring end-to-end grant
          management with AI workflows to its team. From finding and matching
          opportunities to compliance review and funder reporting, HubLife helps
          the organization pursue more funding for the youth it serves.
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Grid of AWS service groups behind the platform. The analytics group is
 * highlighted as the featured capability.
 *
 * @returns {import('react').ReactElement} The architecture grid
 */
function ArchitectureGrid() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        gap: 2,
        mb: 3,
      }}
    >
      {architectureGroups.map((group) => {
        const Icon = group.icon;
        return (
          <Box
            key={group.heading}
            sx={{
              ...cardSx,
              p: 2.25,
              ...(group.featured && {
                borderColor:
                  'color-mix(in srgb, var(--color-primary) 60%, transparent)',
                background:
                  'linear-gradient(160deg, color-mix(in srgb, var(--color-primary) 9%, var(--color-card)), var(--color-card) 65%)',
              }),
            }}
          >
            <Typography
              variant="h3"
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--color-primary)',
                mb: 1.25,
              }}
            >
              <Icon aria-hidden="true" sx={{ fontSize: 18 }} />
              {group.heading}
              {group.featured && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.625rem',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    color: 'var(--color-primary-text)',
                    backgroundColor: 'var(--color-primary)',
                    borderRadius: '100px',
                    px: 1,
                    py: 0.25,
                  }}
                >
                  Featured
                </Box>
              )}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {group.items.map(([service, rest]) => (
                <Box
                  component="li"
                  key={service}
                  sx={{
                    fontSize: '0.84375rem',
                    lineHeight: 1.55,
                    color: 'var(--color-text-muted)',
                    py: 1.1,
                    borderTop: '1px solid var(--color-border)',
                    '&:first-of-type': { borderTop: 'none', pt: 0 },
                  }}
                >
                  <Box
                    component="strong"
                    sx={{ color: 'var(--color-heading)' }}
                  >
                    {service}
                  </Box>
                  {rest}
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * At-a-glance summary of the engagement.
 *
 * @returns {import('react').ReactElement} The summary card
 */
function AtAGlance() {
  return (
    <Box sx={{ ...cardSx, p: 2.25, mb: 4 }}>
      <Typography
        variant="h2"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--color-text-muted)',
          mb: 1.25,
        }}
      >
        At a glance
      </Typography>
      <Box
        component="dl"
        sx={{
          m: 0,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: 1.5,
          rowGap: 1,
          fontSize: '0.8125rem',
        }}
      >
        {atAGlance.map(([term, value]) => (
          <Box key={term} sx={{ display: 'contents' }}>
            <Box component="dt" sx={{ color: 'var(--color-text-muted)' }}>
              {term}
            </Box>
            <Box
              component="dd"
              sx={{ m: 0, fontWeight: 600, color: 'var(--color-text)' }}
            >
              {value}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Case Study: Every Call Captured, Every Youth Served
 *
 * How a California nonprofit serving homeless youth modernized intake with
 * Amazon Connect: bilingual IVR, voicemail transcription, SMS intake and a
 * customer analytics dashboard in Amazon Quick Sight. Uses StoryPageLayout so
 * the AWS-funded POC "Let's Connect" form sits in a sticky sidebar, matching
 * the Rising Routes Alliance case study.
 *
 * @returns {import('react').ReactElement} Nonprofit Amazon Connect case study page
 */
function CaseStudyNonprofitConnect() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${PAGE_TITLE}: An Amazon Connect Case Study`,
    description: PAGE_DESCRIPTION,
    author: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.rsmc.ai/images/r-logo.png',
      },
    },
    image: PAGE_IMAGE,
    datePublished: '2026-02-13',
    dateModified: '2026-10-05',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': PAGE_URL,
    },
  };

  return (
    <>
      <Helmet>
        <title>{`${PAGE_TITLE}: An Amazon Connect Case Study | RSMC`}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          property="og:title"
          content={`${PAGE_TITLE}: An Amazon Connect Case Study`}
        />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={PAGE_IMAGE} />
        <meta
          name="twitter:title"
          content={`${PAGE_TITLE}: An Amazon Connect Case Study`}
        />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="Amazon Connect, nonprofit, homeless youth, bilingual IVR, voicemail transcription, SMS intake, Amazon Quick Sight, Amazon Athena, AWS partner, case study, RSMC"
        />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <StoryPageLayout
        title={PAGE_TITLE}
        subtitle="An Amazon Connect Case Study"
        formHeading="Let's Connect"
        formFilePath="/forms/aws-funded-poc-form.txt"
        heroContent={<CallFlowIllustration />}
        errorMessage="Unable to load contact form"
        loadingMessage="Loading contact form..."
      >
        <Box component="article">
          {/* Article Title */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              color: 'var(--color-heading)',
              mb: 3,
            }}
          >
            How a California nonprofit serving homeless youth modernized intake
            with Amazon Connect
          </Typography>

          {/* Editorial meta row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 4,
            }}
          >
            {['Nonprofit', 'California', '2026'].map((item, index) => (
              <Box
                key={item}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
              >
                {index > 0 && (
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'var(--color-primary)',
                    }}
                  />
                )}
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Deck */}
          <Typography
            sx={{
              ...paragraphSx,
              fontSize: '1.125rem',
              color: 'var(--color-text-muted)',
              mb: 4,
            }}
          >
            When a young person calls looking for a safe place to sleep, that
            call matters. This nonprofit built a communications platform that
            makes sure every call is captured.
          </Typography>

          <Typography sx={paragraphSx}>
            A California nonprofit serving homeless youth wanted every call from
            youth, volunteers, and donors to reach the right person as it grew
            to house more than 200 young people each night. Working with Amazon
            Web Services (AWS) and AWS Partner{' '}
            <TextLink href="/">Ready Secure Managed Cloud</TextLink> (RSMC), the
            nonprofit deployed{' '}
            <TextLink href="https://aws.amazon.com/products/connect/customer/omnichannel/">
              Amazon Connect
            </TextLink>
            , a cloud contact center, with automated voicemail transcription,
            bilingual call flow in English and Spanish, text-based intake, and a
            customer analytics dashboard in Amazon Quick Sight.
          </Typography>

          <KeyResults />

          {/* The Opportunity */}
          <Kicker>The Opportunity</Kicker>
          <Typography variant="h2" sx={headingSx}>
            Scaling communications for a growing youth shelter network
          </Typography>
          <Typography sx={paragraphSx}>
            The organization serves more than 400 transition-age youth each year
            across multiple shelter and housing sites in California. Growth has
            been rapid, driven by the needs of the community.
          </Typography>

          <GrowthStrip />

          <Typography sx={paragraphSx}>
            As the network grew, so did calls from youth seeking shelter,
            volunteers offering support, and donors ready to give. The lean team
            had resourcefully managed these calls on mobile devices, when
            leadership saw an opportunity to centralize communications so that
            every donor call, volunteer inquiry, and youth intake request would
            be captured, routed, and logged automatically.
          </Typography>
          <Typography sx={paragraphSx}>
            Because the organization serves a region with a significant
            Spanish-speaking population, leadership also prioritized a bilingual
            intake path so every young person and family could get support in
            their preferred language. Affordability was a core design
            requirement too.
          </Typography>

          {/* The Solution */}
          <Kicker>The Solution</Kicker>
          <Typography variant="h2" sx={headingSx}>
            A bilingual cloud contact center built on Amazon Connect
          </Typography>
          <Typography sx={paragraphSx}>
            Following a series of modernization workshops, the nonprofit
            selected RSMC to design and implement a cloud communications
            platform. RSMC chose Amazon Connect as the foundation because it is
            fully managed, priced on a pay-per-use basis, and simple for a small
            team to adopt.
          </Typography>
          <Typography sx={paragraphSx}>
            &ldquo;For nonprofits, any modernization project has to be
            affordable and easy to use. Amazon Connect gave this organization an
            enterprise-grade contact center that fits a nonprofit budget,&rdquo;
            says Rochelle Manns, founder and CIO of RSMC.
          </Typography>

          <CallFlowSteps />

          <SolutionFeatures />

          <AnalyticsSpotlight />

          <PullQuote
            quote="Amazon Connect completely changed how we operate. Now, every voicemail is automatically transcribed and emailed to our intake team, who can immediately take action."
            speaker="CEO"
            organization="California nonprofit serving homeless youth"
          />

          {/* The Outcome */}
          <Kicker>The Outcome</Kicker>
          <Typography variant="h2" sx={headingSx}>
            Every call captured, well within budget
          </Typography>
          <Typography sx={paragraphSx}>
            The results were immediate. The nonprofit now achieves 100 percent
            voicemail and call capture across all contact types, including
            during peak periods. Every voicemail is automatically transcribed
            and delivered to the intake team, so staff can respond quickly to
            young people who need shelter or support.
          </Typography>
          <Typography sx={paragraphSx}>
            Giving youth the option to receive intake forms by text has saved
            the intake team 2 hours every day on daily intake operations. Young
            people can start intake right from their own phones, which helps the
            organization meet youth where they are. Staff put that time back
            into direct service for the young people they support.
          </Typography>
          <Typography sx={paragraphSx}>
            Pay-per-use pricing keeps the platform aligned with the
            organization&rsquo;s budget. Monthly communications costs have
            stayed under $100, more than 60 percent below the $250 budget, with
            no per-seat licenses or long-term contracts. With bilingual
            self-service and SMS intake in place, Spanish-speaking youth and
            families can access the same quality of service at the moment they
            need it most.
          </Typography>
          <Typography sx={paragraphSx}>
            The customer analytics dashboard gives leadership clear visibility
            into call volume, peak hours, caller journeys through the IVR, and
            how each call ends, including transfers and voicemail. These
            insights help the team plan staffing around peak periods and keep
            improving the intake experience for youth and families.
          </Typography>

          <NextStepCallout />

          {/* Architecture highlights */}
          <Kicker>Architecture highlights</Kicker>
          <Typography variant="h2" sx={headingSx}>
            The AWS services behind the platform
          </Typography>
          <Typography sx={paragraphSx}>
            RSMC designed an event-driven architecture that extends Amazon
            Connect with voicemail processing, bilingual transcription, SMS
            intake, customer analytics, and full operational monitoring.
          </Typography>

          <ArchitectureGrid />

          <AtAGlance />

          {/* Visual separator - R logo */}
          <Box sx={{ textAlign: 'center', mt: 6, mb: 2 }}>
            <Box
              component="img"
              src="/images/r-logo.png"
              alt="Ready Secure Managed Cloud"
              sx={{ width: '50px', height: 'auto', opacity: 0.7 }}
            />
          </Box>
        </Box>
      </StoryPageLayout>
    </>
  );
}

export default CaseStudyNonprofitConnect;
